import { LitElement, html, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-aside-layout.inner.pcss?inline';
import { whenParsed } from '../../utilities/when-parsed';

/**
 * The Aside Layout component.
 * @element se-aside-layout'
 */
@customElement('se-aside-layout')
export class SEAsideLayout extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });

  /**
   * Whether an aside was slotted in. Held as state rather than read inline in
   * render(): in the critical bundle this component upgrades mid-parse, when
   * the light-DOM children do not exist yet, and render() would otherwise
   * settle on "no aside" and never re-run. Without the `aside` class the
   * wrapper never becomes a flex row, so the aside drops below the content.
   * See utilities/when-parsed.ts (SE-1004).
   */
  @state() private hasAside = false;

  async firstUpdated(): Promise<void> {
    await whenParsed();

    this.hasAside = !!this.querySelector('[slot="aside"]');
  }

  render(): TemplateResult {
    return html`
      <div class="wrapper ${this.hasAside ? 'aside' : ''}">
        <div class="main-content">
          <slot name="main-content"></slot>
        </div>
        <aside class="aside-content">
          <slot name="aside"></slot>
        </aside>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'aside-layout': SEAsideLayout;
  }
}
